import { IdEventV4 } from 'models/event'

/**
 * Maps each smart-signal column id to a predicate that returns whether the column's
 * underlying signal is positively present for a given event.
 *
 * Used by surfaces that auto-hide signal columns with no positive value across a set
 * of events (the visitor event list). Each predicate reads the column's own backing
 * field, so it mirrors the column factory's cell logic without depending on any
 * separate signal-key mapping.
 */
export const SIGNAL_COLUMN_PREDICATES: Record<string, (event: IdEventV4) => boolean> = {
  bot: (e) => e.bot === 'bad',
  incognito: (e) => !!e.incognito,
  vpn: (e) => !!e.vpn,
  tor: (e) => !!e.ip_blocklist?.tor_node,
  browserTampering: (e) => !!e.tampering,
  virtualMachine: (e) => !!e.virtual_machine,
  privacySettings: (e) => !!e.privacy_settings,
  developerTools: (e) => !!e.developer_tools,
  jailbroken: (e) => !!e.jailbroken,
  emulator: (e) => !!e.emulator,
  simulator: (e) => !!e.simulator,
  rootedDevice: (e) => !!e.root_apps,
  clonedApp: (e) => !!e.cloned_app,
  frida: (e) => !!e.frida,
  locationSpoofing: (e) => !!e.location_spoofing,
  ipBlocklist: (e) => !!e.ip_blocklist && Object.values(e.ip_blocklist).some((v) => v === true),
  mitmAttack: (e) => !!e.mitm_attack,
  deviceRarity: (e) => !!e.rare_device,
  residentialProxy: (e) => !!e.proxy,
  activeCall: (e) => !!e.active_call,
}

/** Column ids that represent smart signals (subject to active-signal auto-hiding). */
export const SIGNAL_COLUMN_IDS = Object.keys(SIGNAL_COLUMN_PREDICATES)

/**
 * Returns the set of signal column ids that are positively present in at least one of
 * the provided events.
 */
export function getActiveSignalColumnIds(events: IdEventV4[]): Set<string> {
  const active = new Set<string>()
  for (const id of SIGNAL_COLUMN_IDS) {
    const predicate = SIGNAL_COLUMN_PREDICATES[id]
    if (events.some(predicate)) {
      active.add(id)
    }
  }
  return active
}
